import { PRICING_MODELS, type PlanInputFormUsageProduct, type UsageProductVariantInput } from '@vy/types';
import type { Nullable } from '@vy/utils';
import { isEmptyArray, isNullOrUndefined, raiseUnreachable, replaceInIndex } from '@vy/utils';
import { useFormContext } from 'react-hook-form';
import type { UsagePricingOption } from '../../../../utils/types/usage-pricing-options';
import { USAGE_PRICING_OPTIONS } from '../../../../utils/types/usage-pricing-options';
import type { PlanInputForm } from '../create-plan-page-components/CreatePlanForm/create-plan-form-types/plan-input-form';
import {
  getDefaultPerUnitUsageProduct,
  getDefaultPerUnitUsageProductVariant,
  getDefaultRevShareProductVariant,
  getDefaultRevShareUsageProduct,
  getDefaultTieredSubscriptionUsageProduct,
  getDefaultTieredSubscriptionUsageProductVariant,
  getDefaultTiersUsageProduct,
  getDefaultTiersUsageProductVariant,
  getDefaultVolumeUsageProduct,
  getDefaultVolumeUsageProductVariant,
} from '../create-plan-page-consts/add-item-button-consts';

function supportsCommitment(pricingOption: UsagePricingOption): boolean {
  return pricingOption === USAGE_PRICING_OPTIONS.PER_UNIT || pricingOption === USAGE_PRICING_OPTIONS.TIERS;
}

function supportsBasePrice(pricingOption: UsagePricingOption): boolean {
  return pricingOption !== USAGE_PRICING_OPTIONS.TIERED_SUBSCRIPTION;
}

export function useUsagePricingOption(productIndex: number, productVariantIndex: number) {
  const { setValue, getValues } = useFormContext<PlanInputForm>();

  return (event: React.ChangeEvent<object>, value: Nullable<UsagePricingOption>) => {
    event.preventDefault();

    if (isNullOrUndefined(value)) {
      return;
    }

    const currentProduct = getValues(`usageProducts.${productIndex}`);
    const currentVariant = currentProduct?.variants?.[productVariantIndex];
    const productDefaults = getUpdatedProduct(value);
    const variantTemplate = getUpdatedVariant(value, currentVariant);

    const updatedVariant: UsageProductVariantInput = {
      ...currentVariant,
      ...variantTemplate,
      id: currentVariant?.id ?? variantTemplate.id,
      name: currentVariant?.name ?? variantTemplate.name,
      meterId: currentVariant?.meterId ?? variantTemplate.meterId,
    };

    const updatedVariants = isEmptyArray(currentProduct.variants)
      ? [updatedVariant]
      : replaceInIndex(currentProduct.variants, productVariantIndex, updatedVariant);

    const productWithPreservedData: PlanInputFormUsageProduct = {
      ...currentProduct,
      ...productDefaults,
      id: currentProduct.id ?? productDefaults.id,
      variants: updatedVariants,
      commitment: supportsCommitment(value) ? currentProduct.commitment : undefined,
      basePrice: supportsBasePrice(value) ? currentProduct.basePrice : undefined,
      advancePayment: productDefaults.advancePayment,
      proRata: currentProduct.proRata,
      isCalendarAligned: currentProduct.isCalendarAligned,
    };

    setValue(`usageProducts.${productIndex}`, productWithPreservedData);
  };
}

function getUpdatedProduct(pricingOption: UsagePricingOption) {
  switch (pricingOption) {
    case USAGE_PRICING_OPTIONS.TIERS:
      return getDefaultTiersUsageProduct();
    case USAGE_PRICING_OPTIONS.VOLUME:
      return getDefaultVolumeUsageProduct();
    case USAGE_PRICING_OPTIONS.PER_UNIT:
      return getDefaultPerUnitUsageProduct();
    case USAGE_PRICING_OPTIONS.TIERED_SUBSCRIPTION:
      return getDefaultTieredSubscriptionUsageProduct();
    case USAGE_PRICING_OPTIONS.REVENUE_SHARE:
      return getDefaultRevShareUsageProduct();
    default:
      return raiseUnreachable(pricingOption);
  }
}

function getUpdatedVariant(
  pricingOption: UsagePricingOption,
  currentVariant?: UsageProductVariantInput,
): UsageProductVariantInput {
  switch (pricingOption) {
    case USAGE_PRICING_OPTIONS.TIERS:
      return getDefaultTiersUsageProductVariant();
    case USAGE_PRICING_OPTIONS.VOLUME:
      return getDefaultVolumeUsageProductVariant();
    case USAGE_PRICING_OPTIONS.PER_UNIT:
      return getDefaultPerUnitUsageProductVariant();
    case USAGE_PRICING_OPTIONS.TIERED_SUBSCRIPTION:
      return getTieredSubscriptionVariant(currentVariant);
    case USAGE_PRICING_OPTIONS.REVENUE_SHARE:
      return getDefaultRevShareProductVariant();
    default:
      return raiseUnreachable(pricingOption);
  }
}

function getTieredSubscriptionVariant(
  currentVariant: UsageProductVariantInput | undefined,
): UsageProductVariantInput {
  if (isNullOrUndefined(currentVariant)) {
    return getDefaultTieredSubscriptionUsageProductVariant();
  }

  if (currentVariant.pricing.pricingModel === PRICING_MODELS.TieredSubscription) {
    return getDefaultTieredSubscriptionUsageProductVariant(
      currentVariant.pricing.priceConfiguration?.tierLock,
    );
  }

  return getDefaultTieredSubscriptionUsageProductVariant();
}
